import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// SEC-CSRF-001 (Segurança pré-produção, Pacote 3) — double-submit cookie.
// O backend, ao autenticar por Bearer explícito (caso de praticamente todo
// tráfego legítimo do LOOTUS, já que o token acima sempre vai junto),
// nunca exige CSRF — só quando a autenticação cai para o cookie
// `access_token` (ex.: um site de terceiro fazendo o navegador da vítima
// reenviar o cookie, sem conseguir ler localStorage nem este header). Ainda
// assim, o header é enviado sempre que existir o cookie `csrf_token`
// (JS-readable de propósito — ver _set_session_cookies em server.py) e o
// método for mutante: mais simples e barato que decidir aqui se o backend
// vai preferir Bearer ou cookie, e inofensivo quando ignorado. Ponto ÚNICO
// de injeção — nunca endpoint a endpoint — e escopado a esta instância do
// axios, que só chama a própria API do LOOTUS (baseURL acima); nunca é
// enviado a domínios externos (Asaas, Mercado Pago), que não passam por
// este cliente.
const CSRF_HEADER_NAME = "X-CSRF-Token";
const CSRF_COOKIE_NAME = "csrf_token";
const CSRF_PROTECTED_METHODS = new Set(["post", "put", "patch", "delete"]);

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  const method = (cfg.method || "get").toLowerCase();
  if (CSRF_PROTECTED_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) cfg.headers[CSRF_HEADER_NAME] = csrf;
  }
  return cfg;
});

// Correções de homologação (P1) — o bloqueio financeiro é um ESTADO DE
// NEGÓCIO esperado (empresa com mensalidade vencida há >=15 dias), nunca um
// erro de verdade — nunca deve virar "Uncaught runtime error" nem exigir
// F5 para o app se recuperar. O backend sinaliza esse 403 específico com
// `detail.code === "financial_access_blocked"` (ver get_current_user em
// server.py) — nunca por parsing frágil da mensagem textual. AuthContext
// registra aqui um handler que atualiza o usuário em memória assim que
// qualquer chamada da aplicação encontra esse código, então NENHUMA tela
// individual precisa tratar esse caso sozinha. A rejeição da Promise
// continua propagando normalmente (nunca escondida) para quem já trata seu
// próprio erro — este handler só é um efeito colateral a mais.
let _onFinancialAccessBlocked = null;
export function setFinancialAccessBlockedHandler(fn) { _onFinancialAccessBlocked = fn; }

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 403 && err?.response?.data?.detail?.code === "financial_access_blocked") {
      _onFinancialAccessBlocked?.();
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Erro inesperado";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object" && typeof d.message === "string") return d.message;
  return JSON.stringify(d);
}

export const money = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const fmtDate = (v) => {
  if (!v) return "";
  const [y, m, d] = String(v).slice(0, 10).split("-");
  if (!y) return v;
  return `${d}/${m}/${y}`;
};

export const fmtHours = (h) => {
  const total = Math.round((Number(h) || 0) * 60);
  const H = String(Math.floor(total / 60)).padStart(2, "0");
  const M = String(total % 60).padStart(2, "0");
  return `${H}:${M}h`;
};
